import { useLingui } from '@lingui/react/macro'
import { Button } from 'designSystem/components/Button'
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from 'designSystem/components/DropdownMenu'
import {
  KNOWLEDGE_DOC_FILTER_CHANGE,
  KNOWLEDGE_DOC_OPTIONS_CHANGE,
  useGeckoAnalytics,
} from 'features/analytics'
import { useDocumentTabDataContext } from 'features/knowledgeDocumentViewer/contexts/DocumentTabDataContext'
import { useKnowledgeDocumentDataContext } from 'features/knowledgeDocumentViewer/contexts/KnowledgeDocumentDataContext'
import { useKnowledgeDocumentUXContext } from 'features/knowledgeDocumentViewer/contexts/KnowledgeDocumentUXContext'
import {
  FilterOption,
  TooltipOption,
  viewLayoutSchema,
} from 'features/knowledgeDocumentViewer/types'
import { shouldForceHideDocumentBoxes } from 'features/knowledgeDocumentViewer/types/ocrViewer'
import { effectiveLinkHighlights } from 'features/knowledgeDocumentViewer/utils/viewerPreferencesStorage'
import { useGeckoPermissions } from 'features/permissions'
import { Settings } from 'lucide-react'
import { PERMISSION_CANTILEVER_POWER_FIM_VIEWER } from 'types'

const LINK_HIGHLIGHT_KEYS = ['asset', 'assetClickable', 'docReference'] as const
const LIVE_PROCESS_DATA_OPTION = 'showLiveProcessData'

export function SettingsMenu() {
  const { t } = useLingui()
  const { trackEvent } = useGeckoAnalytics()
  const { hasPermissions } = useGeckoPermissions()
  const canSeeLiveProcessData = hasPermissions([
    PERMISSION_CANTILEVER_POWER_FIM_VIEWER,
  ])
  const {
    tooltipOptions,
    setTooltipOptions,
    linkHighlights,
    setLinkHighlights,
    filterOptions,
    setFilterOptions,
    viewLayout,
    setViewLayout,
    graphMode,
    setGraphMode,
    canUsePhotoMode,
    photoMode,
    setPhotoMode,
    showLiveProcessData,
    setShowLiveProcessData,
    canSeeSubjects,
    canViewGraph,
  } = useKnowledgeDocumentUXContext()
  const { totalPages } = useDocumentTabDataContext()
  const { showDocumentOcrBoxes } = useKnowledgeDocumentDataContext()
  const forceHideDocumentBoxes =
    shouldForceHideDocumentBoxes(showDocumentOcrBoxes)

  const autoOpenNewTab = tooltipOptions.includes(TooltipOption.AutoOpenNewTab)
  const showTooltips = tooltipOptions.includes(TooltipOption.ShowTooltips)
  const showOcrBoxes =
    !forceHideDocumentBoxes && filterOptions !== FilterOption.RemoveBoxes
  const isMultiPage = totalPages > 1
  const shownLinkHighlights = effectiveLinkHighlights(linkHighlights, photoMode)

  const handleViewLayoutChange = (value: string) => {
    const result = viewLayoutSchema.safeParse(value)
    if (!result.success) return
    const newLayout = result.data
    if (newLayout === viewLayout) return

    setViewLayout(newLayout)

    const added: TooltipOption[] = []
    const removed: TooltipOption[] = []

    if (newLayout === 'twoPage') {
      const newOptions = [...tooltipOptions]
      if (!newOptions.includes(TooltipOption.ShowMultiplePages)) {
        newOptions.push(TooltipOption.ShowMultiplePages)
        added.push(TooltipOption.ShowMultiplePages)
      }
      if (!newOptions.includes(TooltipOption.MultiPageTurner)) {
        newOptions.push(TooltipOption.MultiPageTurner)
        added.push(TooltipOption.MultiPageTurner)
      }
      setTooltipOptions(newOptions)
      trackEvent(KNOWLEDGE_DOC_OPTIONS_CHANGE.title, {
        options: newOptions,
        added_options: added,
        removed_options: removed,
      })
    } else if (viewLayout === 'twoPage') {
      if (tooltipOptions.includes(TooltipOption.ShowMultiplePages)) {
        removed.push(TooltipOption.ShowMultiplePages)
      }
      if (tooltipOptions.includes(TooltipOption.MultiPageTurner)) {
        removed.push(TooltipOption.MultiPageTurner)
      }
      const newOptions = tooltipOptions.filter(
        (opt) =>
          opt !== TooltipOption.ShowMultiplePages &&
          opt !== TooltipOption.MultiPageTurner
      )
      setTooltipOptions(newOptions)
      trackEvent(KNOWLEDGE_DOC_OPTIONS_CHANGE.title, {
        options: newOptions,
        added_options: added,
        removed_options: removed,
      })
    }
  }

  const handleAutoOpenNewTabChange = (checked: boolean) => {
    const currentlyEnabled = tooltipOptions.includes(
      TooltipOption.AutoOpenNewTab
    )

    if (checked === currentlyEnabled) {
      return
    }

    let newOptions: TooltipOption[]
    const added: TooltipOption[] = []
    const removed: TooltipOption[] = []

    if (checked) {
      newOptions = [...tooltipOptions, TooltipOption.AutoOpenNewTab]
      added.push(TooltipOption.AutoOpenNewTab)
    } else {
      newOptions = tooltipOptions.filter(
        (opt) => opt !== TooltipOption.AutoOpenNewTab
      )
      removed.push(TooltipOption.AutoOpenNewTab)
    }

    trackEvent(KNOWLEDGE_DOC_OPTIONS_CHANGE.title, {
      options: newOptions,
      added_options: added,
      removed_options: removed,
    })
    setTooltipOptions(newOptions)
  }

  const handleShowTooltipsChange = (checked: boolean) => {
    const currentlyEnabled = tooltipOptions.includes(TooltipOption.ShowTooltips)

    if (checked === currentlyEnabled) {
      return
    }

    let newOptions: TooltipOption[]
    const added: TooltipOption[] = []
    const removed: TooltipOption[] = []

    if (checked) {
      newOptions = [...tooltipOptions, TooltipOption.ShowTooltips]
      added.push(TooltipOption.ShowTooltips)
    } else {
      newOptions = tooltipOptions.filter(
        (opt) => opt !== TooltipOption.ShowTooltips
      )
      removed.push(TooltipOption.ShowTooltips)
    }

    trackEvent(KNOWLEDGE_DOC_OPTIONS_CHANGE.title, {
      options: newOptions,
      added_options: added,
      removed_options: removed,
    })
    setTooltipOptions(newOptions)
  }

  const handleLinkHighlightChange = (
    key: keyof typeof linkHighlights,
    checked: boolean
  ) => {
    if (checked === linkHighlights[key]) {
      return
    }

    const newHighlights = { ...linkHighlights, [key]: checked }

    trackEvent(KNOWLEDGE_DOC_OPTIONS_CHANGE.title, {
      options: LINK_HIGHLIGHT_KEYS.filter((k) => newHighlights[k]),
      added_options: checked ? [key] : [],
      removed_options: checked ? [] : [key],
    })
    setLinkHighlights(newHighlights)
  }

  const handleLiveProcessDataChange = (checked: boolean) => {
    if (checked === showLiveProcessData) {
      return
    }

    trackEvent(KNOWLEDGE_DOC_OPTIONS_CHANGE.title, {
      options: checked ? [LIVE_PROCESS_DATA_OPTION] : [],
      added_options: checked ? [LIVE_PROCESS_DATA_OPTION] : [],
      removed_options: checked ? [] : [LIVE_PROCESS_DATA_OPTION],
    })
    setShowLiveProcessData(checked)
  }

  const handleGraphModeChange = (checked: boolean): void => {
    if (!canViewGraph) return
    setGraphMode(checked)
    trackEvent(KNOWLEDGE_DOC_OPTIONS_CHANGE.title, {
      options: checked ? ['graphMode'] : [],
      added_options: checked ? ['graphMode'] : [],
      removed_options: checked ? [] : ['graphMode'],
    })
  }

  const handlePhotoModeChange = (checked: boolean): void => {
    if (!canUsePhotoMode) return
    setPhotoMode(checked)
    trackEvent(KNOWLEDGE_DOC_OPTIONS_CHANGE.title, {
      options: checked ? ['photoMode'] : [],
      added_options: checked ? ['photoMode'] : [],
      removed_options: checked ? [] : ['photoMode'],
    })
  }

  const handleOcrBoxesChange = (checked: boolean) => {
    if (forceHideDocumentBoxes) {
      return
    }

    const currentlyShowingBoxes = filterOptions !== FilterOption.RemoveBoxes

    if (checked === currentlyShowingBoxes) {
      return
    }

    const newFilter = checked
      ? FilterOption.ShowConfirmed
      : FilterOption.RemoveBoxes

    trackEvent(KNOWLEDGE_DOC_FILTER_CHANGE.title, {
      filter_option: newFilter,
      previous_filter: filterOptions,
    })
    setFilterOptions(newFilter)
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant='inputRaised' size='icon-lg' title={t`Settings`}>
          <Settings size={18} strokeWidth={1.5} />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align='start'>
        <DropdownMenuCheckboxItem
          checked={showTooltips}
          onCheckedChange={handleShowTooltipsChange}>
          {t`Show tooltip when hovering on links`}
        </DropdownMenuCheckboxItem>
        <DropdownMenuCheckboxItem
          checked={autoOpenNewTab}
          onCheckedChange={handleAutoOpenNewTabChange}>
          {t`Always open links in new tab`}
        </DropdownMenuCheckboxItem>
        <DropdownMenuSeparator />
        {canSeeSubjects && (
          <>
            <DropdownMenuLabel>{t`Link highlights`}</DropdownMenuLabel>
            <DropdownMenuCheckboxItem
              checked={shownLinkHighlights.asset}
              disabled={photoMode}
              onCheckedChange={(checked) =>
                handleLinkHighlightChange('asset', checked)
              }>
              {t`Highlight Assets`}
            </DropdownMenuCheckboxItem>
            <DropdownMenuCheckboxItem
              checked={shownLinkHighlights.assetClickable}
              disabled={photoMode}
              onCheckedChange={(checked) =>
                handleLinkHighlightChange('assetClickable', checked)
              }>
              {t`Clickable Assets`}
            </DropdownMenuCheckboxItem>
            <DropdownMenuCheckboxItem
              checked={linkHighlights.docReference}
              onCheckedChange={(checked) =>
                handleLinkHighlightChange('docReference', checked)
              }>
              {t`Document references`}
            </DropdownMenuCheckboxItem>
          </>
        )}
        <DropdownMenuCheckboxItem
          checked={showOcrBoxes}
          disabled={forceHideDocumentBoxes}
          onCheckedChange={handleOcrBoxesChange}>
          {t`OCR boxes`}
        </DropdownMenuCheckboxItem>
        {canSeeLiveProcessData && (
          <DropdownMenuCheckboxItem
            checked={showLiveProcessData}
            onCheckedChange={handleLiveProcessDataChange}>
            {t`Show live process data`}
          </DropdownMenuCheckboxItem>
        )}
        {canViewGraph && (
          <DropdownMenuCheckboxItem
            checked={graphMode}
            onCheckedChange={handleGraphModeChange}>
            {t`Graph mode`}
          </DropdownMenuCheckboxItem>
        )}
        {canUsePhotoMode && (
          <DropdownMenuCheckboxItem
            checked={photoMode}
            onCheckedChange={handlePhotoModeChange}>
            {t`Photo mode`}
          </DropdownMenuCheckboxItem>
        )}
        {isMultiPage && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuLabel>{t`Page layout`}</DropdownMenuLabel>
            <DropdownMenuRadioGroup
              value={viewLayout}
              onValueChange={handleViewLayoutChange}>
              <DropdownMenuRadioItem value='continuous'>
                {t`Continuous scroll`}
              </DropdownMenuRadioItem>
              <DropdownMenuRadioItem value='single'>
                {t`Single page`}
              </DropdownMenuRadioItem>
              <DropdownMenuRadioItem value='twoPage'>
                {t`Two-page spread`}
              </DropdownMenuRadioItem>
            </DropdownMenuRadioGroup>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
